import { useEffect, useMemo, useState } from "react";
import { RefreshCw, SlidersHorizontal, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DataTable, SearchBar } from "../Table";
import Modal from "../Modal/Modal";
import {
  bulkUpdateAdminOutletRadius,
  getAdminOutlets,
} from "../../../services/api";
import { useNotification } from "../../../hooks/useNotification";
import OutletRadiusDrawer from "../SessionsView/OutletRadiusDrawer";
import "./OutletRadiusView.css";

const PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DEFAULT_RADIUS_METERS = 500;

const safeString = (value) => String(value || "").trim();

const normalizeRadiusInput = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isInteger(parsed)) return null;
  return parsed;
};

const formatRadiusLabel = (value, defaultRadiusMeters = DEFAULT_RADIUS_METERS) => {
  const numericValue = Number(value);
  const normalizedDefaultRadius = Number.parseInt(defaultRadiusMeters, 10);
  const safeDefaultRadius =
    Number.isInteger(normalizedDefaultRadius) && normalizedDefaultRadius > 0
      ? normalizedDefaultRadius
      : DEFAULT_RADIUS_METERS;

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return `${safeDefaultRadius} m`;
  }
  return `${Math.round(numericValue)} m`;
};

const isDefaultRadius = (value, defaultRadiusMeters = DEFAULT_RADIUS_METERS) => {
  if (value == null || value === "") return true;
  const numericValue = Number(value);
  const normalizedDefaultRadius = Number.parseInt(defaultRadiusMeters, 10);
  const safeDefaultRadius =
    Number.isInteger(normalizedDefaultRadius) && normalizedDefaultRadius > 0
      ? normalizedDefaultRadius
      : DEFAULT_RADIUS_METERS;
  if (!Number.isFinite(numericValue) || numericValue <= 0) return true;
  return Math.round(numericValue) === safeDefaultRadius;
};

export default function OutletRadiusView({ searchQuery = "" }) {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [appliedQuery, setAppliedQuery] = useState(searchQuery.trim());
  const [outlets, setOutlets] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [defaultRadiusMeters, setDefaultRadiusMeters] = useState(DEFAULT_RADIUS_METERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshToken, setRefreshToken] = useState(0);
  const [pageLimit, setPageLimit] = useState(PAGE_SIZE);
  const [pageOffset, setPageOffset] = useState(0);
  const [radiusDrawerOutlet, setRadiusDrawerOutlet] = useState(null);
  const [bulkPanelOpen, setBulkPanelOpen] = useState(false);
  const [bulkRadiusInput, setBulkRadiusInput] = useState("");
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkIntentRadius, setBulkIntentRadius] = useState(null);

  useEffect(() => {
    setSearchInput(searchQuery);
    setAppliedQuery(searchQuery.trim());
    setPageOffset(0);
  }, [searchQuery]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setAppliedQuery(searchInput.trim());
      setPageOffset(0);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;

    const fetchOutlets = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await getAdminOutlets({
          q: appliedQuery,
          limit: pageLimit,
          offset: pageOffset,
        });

        if (!cancelled) {
          const payload = response || {};
          const rows = Array.isArray(payload.data) ? payload.data : [];
          const nextDefaultRadius = Number.parseInt(payload.default_radius_meters, 10);
          setOutlets(rows);
          setTotalCount(Number.parseInt(payload.count, 10) || rows.length || 0);
          setDefaultRadiusMeters(
            Number.isInteger(nextDefaultRadius) && nextDefaultRadius > 0
              ? nextDefaultRadius
              : DEFAULT_RADIUS_METERS,
          );
        }
      } catch (fetchError) {
        if (!cancelled) {
          setOutlets([]);
          setTotalCount(0);
          const message =
            fetchError?.response?.data?.error ||
            t("admin.outletRadiusPage.failedToLoad");
          setError(message);
          showError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchOutlets();

    return () => {
      cancelled = true;
    };
  }, [appliedQuery, pageLimit, pageOffset, refreshToken, showError, t]);

  const tableColumns = useMemo(
    () => [
      {
        label: t("admin.outletRadiusPage.columns.outlet"),
        sortKey: "name",
      },
      {
        label: t("admin.outletRadiusPage.columns.address"),
        sortKey: "address",
      },
      {
        label: t("admin.outletRadiusPage.columns.radius"),
        sortKey: "allowed_radius_meters",
        headerClassName: "admin-outlet-radius-radius-col",
      },
      {
        label: t("admin.outletRadiusPage.columns.actions"),
        headerClassName: "admin-outlet-radius-actions-col",
      },
    ],
    [t],
  );

  const handleRefresh = () => {
    setRefreshToken((previous) => previous + 1);
  };

  const handleOpenRadiusDrawer = (outlet) => {
    setRadiusDrawerOutlet(outlet);
  };

  const handlePageLimitChange = (event) => {
    const nextLimit = Number.parseInt(event.target.value, 10);
    if (!Number.isInteger(nextLimit) || nextLimit <= 0) return;
    setPageLimit(nextLimit);
    setPageOffset(0);
  };

  const handleToggleBulkPanel = () => {
    setBulkError("");
    setBulkPanelOpen((previous) => !previous);
  };

  const handleBulkApplyRequest = () => {
    const normalizedRadius = normalizeRadiusInput(bulkRadiusInput);

    if (bulkRadiusInput.trim() && normalizedRadius === null) {
      setBulkError(t("admin.outletRadiusPage.invalidBulkNumber"));
      return;
    }

    setBulkError("");
    setBulkIntentRadius(normalizedRadius);
    setBulkConfirmOpen(true);
  };

  const handleBulkApplyConfirm = async () => {
    if (bulkApplying) return;

    setBulkApplying(true);
    setBulkConfirmOpen(false);

    try {
      const response = await bulkUpdateAdminOutletRadius({
        q: appliedQuery,
        allowed_radius_meters: bulkIntentRadius,
      });

      const payload = response || {};
      const updatedCount = Number.parseInt(payload.updated_count, 10);
      const safeUpdatedCount =
        Number.isInteger(updatedCount) && updatedCount >= 0 ? updatedCount : totalCount;

      setBulkRadiusInput("");
      setBulkIntentRadius(null);
      setBulkPanelOpen(false);
      setRefreshToken((previous) => previous + 1);

      showSuccess(
        bulkIntentRadius == null
          ? t("admin.outletRadiusPage.bulkDefaultSuccess", {
              count: safeUpdatedCount,
            })
          : t("admin.outletRadiusPage.bulkSuccess", {
              count: safeUpdatedCount,
            }),
      );
    } catch (updateError) {
      const message =
        updateError?.response?.data?.error ||
        t("admin.outletRadiusPage.bulkFailed");
      showError(message);
      setBulkConfirmOpen(false);
    } finally {
      setBulkApplying(false);
    }
  };

  const handleBulkApplyCancel = () => {
    setBulkConfirmOpen(false);
    setBulkIntentRadius(null);
  };

  const bulkToggleLabel = bulkPanelOpen
    ? t("admin.outletRadiusPage.bulkClose")
    : t("admin.outletRadiusPage.bulkTitle");

  const renderRow = (outlet) => {
    const radiusLabel = formatRadiusLabel(outlet.allowed_radius_meters, defaultRadiusMeters);
    const normalizedSource = String(outlet.radius_source || "").trim().toLowerCase();
    const radiusIsDefault =
      normalizedSource === "default"
        ? true
        : normalizedSource === "outlet"
          ? false
          : isDefaultRadius(outlet.allowed_radius_meters, defaultRadiusMeters);
    const radiusBadgeClass = radiusIsDefault ? "is-default" : "is-custom";

    return (
      <tr key={outlet.id}>
        <td>
          <div className="admin-outlet-radius-cell-stack">
            <strong>{safeString(outlet.name) || t("admin.sessions.na")}</strong>
          </div>
        </td>
        <td>
          <div className="admin-outlet-radius-address">
            <span>{safeString(outlet.address) || t("admin.sessions.na")}</span>
          </div>
        </td>
        <td>
          <div className="admin-outlet-radius-radius-cell">
            <span className={`admin-outlet-radius-radius-pill ${radiusBadgeClass}`}>
              {radiusIsDefault
                ? t("admin.outletRadiusPage.default")
                : t("admin.outletRadiusPage.custom")}
            </span>
            <strong>{radiusLabel}</strong>
          </div>
        </td>
        <td>
          <button
            type="button"
            className="admin-outlet-radius-card-action"
            onClick={() => handleOpenRadiusDrawer(outlet)}
            disabled={bulkApplying}
          >
            {t("admin.outletRadiusPage.configure")}
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className="admin-outlet-radius-page">
      <div className="admin-outlet-radius-hero">
        <div>
          <h2 className="admin-section-title">{t("admin.outletRadiusPage.title")}</h2>
          <p className="admin-section-subtitle">{t("admin.outletRadiusPage.subtitle")}</p>
        </div>

        <div className="admin-outlet-radius-hero-actions">
          <button type="button" className="admin-btn-secondary" onClick={handleRefresh}>
            <RefreshCw size={16} />
            {t("admin.common.refresh")}
          </button>
        </div>
      </div>

      <div className="admin-outlet-radius-toolbar">
        <div className="admin-outlet-radius-search-row">
          <SearchBar
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t("admin.outletRadiusPage.searchPlaceholder")}
            className="admin-outlet-radius-search"
          />
          <p className="admin-outlet-radius-search-hint">
            {t("admin.outletRadiusPage.searchHint")}
          </p>
        </div>

        <div className="admin-outlet-radius-toolbar-actions">
          <button
            type="button"
            className="admin-outlet-radius-bulk-toggle"
            onClick={handleToggleBulkPanel}
            aria-expanded={bulkPanelOpen}
            aria-controls="admin-outlet-radius-bulk-panel"
          >
            {bulkPanelOpen ? <X size={16} /> : <SlidersHorizontal size={16} />}
            {bulkToggleLabel}
          </button>

          <div className="admin-outlet-radius-rows-per-page">
            <label htmlFor="outletRadiusRowsPerPage">
              {t("admin.common.itemsPerPage")}
            </label>
            <select
              id="outletRadiusRowsPerPage"
              value={pageLimit}
              onChange={handlePageLimitChange}
              className="admin-outlet-radius-rows-select"
              disabled={loading || bulkApplying}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          id="admin-outlet-radius-bulk-panel"
          className={`admin-outlet-radius-bulk-panel ${bulkPanelOpen ? "is-open" : ""}`}
          aria-hidden={!bulkPanelOpen}
        >
          <div className="admin-outlet-radius-bulk-panel-inner">
            <div className="admin-outlet-radius-bulk-panel-body">
              <div className="admin-outlet-radius-bulk-panel-head">
                <div>
                  <strong>{t("admin.outletRadiusPage.bulkTitle")}</strong>
                  <span>
                    {t("admin.outletRadiusPage.bulkSubtitle", {
                      count: totalCount,
                    })}
                  </span>
                </div>
              </div>

              <div className="admin-outlet-radius-bulk-grid">
                <div className="admin-outlet-radius-bulk-form">
                  <label htmlFor="bulkRadiusInput">
                    {t("admin.outletRadiusPage.bulkInputLabel")}
                  </label>
                  <input
                    id="bulkRadiusInput"
                    type="number"
                    min={10}
                    max={10000}
                    step={1}
                    value={bulkRadiusInput}
                    onChange={(event) => setBulkRadiusInput(event.target.value)}
                    placeholder={t("admin.outletRadiusPage.bulkPlaceholder")}
                    className="admin-sessions-input admin-outlet-radius-bulk-input"
                    disabled={bulkApplying || loading}
                  />
                  <p className="admin-outlet-radius-bulk-hint">
                    {t("admin.outletRadiusPage.bulkHintPrimary")}
                  </p>
                  <p className="admin-outlet-radius-bulk-hint is-secondary">
                    {t("admin.outletRadiusPage.bulkHintReset")}
                  </p>
                  {bulkError ? <p className="admin-outlet-radius-error">{bulkError}</p> : null}
                </div>

                <div className="admin-outlet-radius-bulk-summary">
                  <span>{t("admin.outletRadiusPage.bulkApplySummary")}</span>
                  <strong>
                    {t("admin.outletRadiusPage.bulkCurrentDefault", {
                      count: totalCount,
                    })}
                  </strong>
                </div>
              </div>

              <div className="admin-outlet-radius-bulk-actions">
                <button
                  type="button"
                  className="admin-btn-primary admin-outlet-radius-bulk-apply"
                  onClick={handleBulkApplyRequest}
                  disabled={bulkApplying || loading}
                >
                  {bulkApplying
                    ? t("admin.common.processing")
                    : t("admin.outletRadiusPage.bulkApply")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {bulkApplying ? (
        <div className="admin-outlet-radius-loading-overlay" aria-busy="true" aria-live="polite">
          <div className="admin-outlet-radius-loading-card">
            <RefreshCw size={24} />
            <strong>{t("admin.common.processing")}</strong>
            <span>{t("admin.outletRadiusPage.bulkProcessing")}</span>
          </div>
        </div>
      ) : null}

      <div className="admin-outlet-radius-table-wrap">
        <DataTable
          data={outlets}
          columns={tableColumns}
          renderRow={renderRow}
          showHeader={false}
          showSearch={false}
          showFilter={false}
          showHeaderActions={false}
          loading={loading || bulkApplying}
          error={error}
          emptyMessage={
            appliedQuery.trim()
              ? t("admin.outletRadiusPage.searchEmpty")
              : t("admin.outletRadiusPage.tableEmpty")
          }
          serverMode
          serverPagination={{
            total: totalCount,
            limit: pageLimit,
            offset: pageOffset,
          }}
          onServerPaginationChange={({ limit, offset }) => {
            setPageLimit(limit);
            setPageOffset(offset);
          }}
          initialItemsPerPage={PAGE_SIZE}
          itemsPerPageOptions={[10, 20, 50]}
          tableClassName="admin-outlet-radius-table admin-users-table"
          containerClassName="admin-outlet-radius-table-card admin-users-section"
        />
      </div>

      <OutletRadiusDrawer
        isOpen={Boolean(radiusDrawerOutlet)}
        outlet={radiusDrawerOutlet}
        onClose={() => setRadiusDrawerOutlet(null)}
        onSaved={() => {
          showSuccess(t("admin.sessions.outletRadius.saveSuccess"));
          setRadiusDrawerOutlet(null);
          setRefreshToken((previous) => previous + 1);
        }}
        t={t}
        showError={showError}
      />

      <Modal
        isOpen={bulkConfirmOpen}
        onClose={handleBulkApplyCancel}
        size="small"
        showCloseButton={false}
        closeOnOverlayClick={!bulkApplying}
        closeOnEscape={!bulkApplying}
      >
        <div className="admin-outlet-radius-confirm">
          <h3>{t("admin.outletRadiusPage.bulkConfirmTitle")}</h3>
          <p>
            {bulkIntentRadius == null
              ? t("admin.outletRadiusPage.bulkConfirmDefault", {
                  count: totalCount,
                })
              : t("admin.outletRadiusPage.bulkConfirmBody", {
                  radius: `${bulkIntentRadius} m`,
                  count: totalCount,
                })}
          </p>
          <div className="admin-outlet-radius-confirm-actions">
            <button
              type="button"
              className="admin-btn-primary admin-outlet-radius-bulk-confirm-apply"
              onClick={handleBulkApplyConfirm}
              disabled={bulkApplying}
            >
              {bulkApplying ? t("admin.common.processing") : t("admin.common.apply")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
